import { memo } from "react";
import { useTheme } from "@repo/tokens";
import { Icon } from "@repo/ui";
import { MathJaxHtml } from "../../../../../../components/mathJax/MathJaxHtml";
import type { QuestionData } from "../../models/GetQuestions";
import { getOptionState } from "../selection/optionState";
import styles from "../selection/SelectionOptions.module.scss";

const CHECKBOX_SIZE = 20;

type Props = {
  content: string;
  isSelected: boolean;
  onToggle: () => void;
  questionData: QuestionData;
  optionIndex: number;
  correctPositions: number[];
};

const MultipleSelectionWebInner = ({
  content,
  isSelected,
  onToggle,
  questionData,
  optionIndex,
  correctPositions,
}: Props) => {
  const { theme } = useTheme();
  const isThisOptionCorrect = correctPositions.includes(optionIndex + 1);
  const state = getOptionState({
    isAttempted: questionData?.isAttempted,
    isSelected,
    isThisOptionCorrect,
  });

  const rowClass =
    state === "correct"
      ? styles.optionCorrect
      : state === "wrong"
        ? styles.optionWrong
        : state === "selected"
          ? styles.optionSelected
          : styles.optionDefault;

  const checkboxColor =
    state === "correct"
      ? theme.colors.success[60]
      : state === "wrong"
        ? theme.colors.error[60]
        : state === "selected"
          ? theme.colors.primary[50]
          : theme.colors.neutral[10];

  const isAttempted = !!questionData?.isAttempted;
  const showCheckboxCheckmark = isSelected;
  const isFilled =
    isSelected &&
    (state === "correct" || state === "wrong" || state === "selected");
  const emptyCheckboxBorder =
    isAttempted && state === "correct" && !isSelected
      ? `2px solid ${theme.colors.success[60]}`
      : "2px solid #14142b";

  const disabled = isAttempted;

  return (
    <button
      type="button"
      className={`${styles.optionRow} ${rowClass}`}
      disabled={disabled}
      onClick={() => {
        if (!disabled) onToggle();
      }}
    >
      <span
        className={styles.checkboxOuter}
        style={{
          border: isFilled ? "none" : emptyCheckboxBorder,
          backgroundColor: isFilled ? checkboxColor : "transparent",
        }}
      >
        {showCheckboxCheckmark && (
          <Icon name="checkedIcon" color="white" size={CHECKBOX_SIZE * 1.5} />
        )}
      </span>
      <span className={styles.optionMath}>
        <MathJaxHtml content={content} className={styles.reviseTextMathStyle} />
      </span>
    </button>
  );
};

export const MultipleSelectionWeb = memo(
  MultipleSelectionWebInner,
  (prev, next) =>
    prev.content === next.content &&
    prev.isSelected === next.isSelected &&
    prev.optionIndex === next.optionIndex &&
    prev.questionData.isAttempted === next.questionData.isAttempted &&
    prev.correctPositions === next.correctPositions &&
    prev.onToggle === next.onToggle,
);
